import React, { useState, useEffect } from 'react';
import { TeamMember, COMMON_TEAM_ROLES } from '@/types/team';
import { Trash2, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@nimbitlabs/ui';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, Button, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Popover, PopoverContent, PopoverTrigger } from '@nimbitlabs/ui';

interface ManageTeamMemberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: TeamMember | null;
  onUpdateMember: (member: TeamMember) => void;
  onRemoveMember: (member: TeamMember) => void;
}

export const ManageTeamMemberModal: React.FC<ManageTeamMemberModalProps> = ({
  open,
  onOpenChange,
  member,
  onUpdateMember,
  onRemoveMember
}) => {
  const [formData, setFormData] = useState<TeamMember>({
    name: '',
    email: '',
    role: 'Developer',
    avatar: ''
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [rolePopoverOpen, setRolePopoverOpen] = useState(false);

  const generateAvatarUrl = (name: string) => {
    if (!name.trim()) return '';
    return `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(name)}`;
  };

  const handleNameChange = (name: string) => {
    setFormData({ 
      ...formData, 
      name,
      avatar: formData.avatar || generateAvatarUrl(name)
    });
  };

  useEffect(() => {
    if (member) {
      setFormData({
        name: member.name,
        email: member.email,
        role: member.role,
        avatar: member.avatar || ''
      });
    }
  }, [member]);

  const handleUpdateMember = () => {
    if (!member || !formData.role) return;
    
    // Only update the role - name, email, avatar come from user record
    onUpdateMember({
      ...member,
      role: formData.role
    });
    onOpenChange(false);
  };

  const handleRemoveMember = () => {
    if (!member) return;
    onRemoveMember(member);
    setShowDeleteDialog(false);
    onOpenChange(false);
  };

  if (!member) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Manage Team Member</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Display-only user info */}
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                {formData.avatar && (
                  <img 
                    src={formData.avatar} 
                    alt={formData.name} 
                    className="h-12 w-12 rounded-full object-cover border-2"
                  />
                )}
                <div>
                  <p className="font-medium">{formData.name}</p>
                  <p className="text-sm text-muted-foreground">{formData.email}</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Name, email, and avatar are managed in User Management
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Popover open={rolePopoverOpen} onOpenChange={setRolePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={rolePopoverOpen}
                    className="w-full justify-between"
                  >
                    {formData.role || "Select or type a role..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search or type custom role..." 
                      value={formData.role}
                      onValueChange={(value) => setFormData({ ...formData, role: value })}
                    />
                    <CommandList>
                      <CommandEmpty>
                        Press Enter to use "{formData.role}"
                      </CommandEmpty>
                      <CommandGroup>
                        {COMMON_TEAM_ROLES.map((role) => (
                          <CommandItem
                            key={role}
                            value={role}
                            onSelect={(currentValue) => {
                              setFormData({ ...formData, role: currentValue });
                              setRolePopoverOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                formData.role === role ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {role}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">Select from common roles or type a custom role</p>
            </div>

            <div className="pt-4 border-t">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                className="w-full"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove Member
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateMember} disabled={!formData.role}>
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {member.name} from this team? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMember} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
